import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Post, PostComment, LikePreviewUser, PostLiker } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { formatDateTime, getInitials, getDepartmentColor } from '../../utils/helpers';
import { postsService } from '../../services/posts.service';
import { isCoAdminOrAdmin, canManageSpotlightPosts } from '../../utils/roles';
import { isVideoUrl } from '../../utils/media';
import './PostCard.css';

interface PostCardProps {
  post: Post;
  onEdit?: (post: Post) => void;
  onDelete?: (postId: string) => void;
}

const PostCard: React.FC<PostCardProps> = ({ post, onEdit, onDelete }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const feedVideoRef = useRef<HTMLVideoElement>(null);
  const departmentColor = getDepartmentColor(post.department || post.createdBy?.department || '');
  const mediaUrl = post.cdnLink?.replace('.r2.devp/', '.r2.dev/');
  const cdnIsVideo = !!(mediaUrl && isVideoUrl(mediaUrl));

  // Reaction state
  const [liked, setLiked] = useState(post.isLikedByUser ?? false);
  const [likeCount, setLikeCount] = useState(post.likeCount ?? 0);
  const [likeLoading, setLikeLoading] = useState(false);

  // Likes preview / modal state
  const [likePreview, setLikePreview] = useState<LikePreviewUser[]>(post.likePreview ?? []);
  const [showLikesModal, setShowLikesModal] = useState(false);
  const [likers, setLikers] = useState<PostLiker[]>([]);
  const [likersLoading, setLikersLoading] = useState(false);
  const [likersLoaded, setLikersLoaded] = useState(false);
  const [likersError, setLikersError] = useState<string | null>(null);

  // Comments state
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState<PostComment[]>([]);
  const [commentCount, setCommentCount] = useState(post.commentCount ?? 0);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const commentInputRef = useRef<HTMLTextAreaElement>(null);

  const isSpotlightPostCard =
    post.category === 'SPOTLIGHT_EOM' ||
    post.category === 'SPOTLIGHT_NEW_EMPLOYEE' ||
    (post.category === 'ANNOUNCEMENT' && post.isNewEmployee);

  const canModify =
    isCoAdminOrAdmin(user?.role) ||
    user?.id === post.createdBy?.id ||
    (canManageSpotlightPosts(user?.role) && isSpotlightPostCard);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync props when post changes (e.g. after feed refresh)
  useEffect(() => {
    setLiked(post.isLikedByUser ?? false);
    setLikeCount(post.likeCount ?? 0);
    setCommentCount(post.commentCount ?? 0);
    setLikePreview(post.likePreview ?? []);
    // Invalidate cached full likers list so the modal re-fetches fresh data next time.
    setLikersLoaded(false);
    setLikers([]);
  }, [post.isLikedByUser, post.likeCount, post.commentCount, post.likePreview]);

  useEffect(() => {
    const el = feedVideoRef.current;
    if (!el || !cdnIsVideo || !mediaUrl) return;
    el.muted = true;
    el.defaultMuted = true;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.play().catch(() => {});
          } else {
            el.pause();
          }
        });
      },
      { threshold: 0.3, rootMargin: '0px 0px -8% 0px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [mediaUrl, cdnIsVideo]);

  // Lock background scroll while the Likes modal is open and compensate for the
  // scrollbar width so the page doesn't visibly shift when it closes.
  useEffect(() => {
    if (!showLikesModal) return;
    const previousBodyOverflow = document.body.style.overflow;
    const previousBodyPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.body.style.paddingRight = previousBodyPaddingRight;
    };
  }, [showLikesModal]);

  const handleEdit = () => {
    setShowMenu(false);
    onEdit?.(post);
  };

  const handleDelete = () => {
    setShowMenu(false);
    setShowDeleteConfirm(true);
  };

  const cancelDelete = () => setShowDeleteConfirm(false);

  const confirmDelete = () => {
    setShowDeleteConfirm(false);
    onDelete?.(post.id);
  };

  const handleLike = useCallback(async () => {
    if (likeLoading) return;
    setLikeLoading(true);
    // Optimistic update
    const newLiked = !liked;
    setLiked(newLiked);
    setLikeCount((c) => c + (newLiked ? 1 : -1));

    // Optimistically reflect the current user in the preview row.
    if (user) {
      setLikePreview((prev) => {
        const without = prev.filter((u) => u.id !== user.id);
        if (newLiked) {
          const me: LikePreviewUser = {
            id: user.id,
            fullName: user.fullName,
            profilePicture: user.profilePicture ?? null,
            department: user.department ?? null,
          };
          return [me, ...without].slice(0, 3);
        }
        return without;
      });
    }
    // Full likers list is now stale.
    setLikersLoaded(false);

    try {
      const result = await postsService.toggleLike(post.id);
      setLiked(result.liked);
      setLikeCount(result.likeCount);
    } catch {
      // Revert on failure
      setLiked(!newLiked);
      setLikeCount((c) => c + (newLiked ? -1 : 1));
      setLikePreview(post.likePreview ?? []);
    } finally {
      setLikeLoading(false);
    }
  }, [likeLoading, liked, post.id, post.likePreview, user]);

  // ─── Likes preview / modal ────────────────────────────────────────────────
  const loadLikers = useCallback(async () => {
    if (likersLoaded || likersLoading) return;
    setLikersLoading(true);
    setLikersError(null);
    try {
      const data = await postsService.getLikers(post.id);
      setLikers(data);
      setLikersLoaded(true);
    } catch {
      setLikersError('Could not load likes. Please try again.');
    } finally {
      setLikersLoading(false);
    }
  }, [likersLoaded, likersLoading, post.id]);

  const openLikesModal = useCallback(() => {
    if (likeCount <= 0) return;
    setShowLikesModal(true);
    loadLikers();
  }, [likeCount, loadLikers]);

  const closeLikesModal = useCallback(() => setShowLikesModal(false), []);

  /**
   * Renders the "Liked by …" line Instagram-style: bold names, comma-separated,
   * with the trailing "N others" also bolded and clickable as a single trigger.
   */
  const renderInstagramSummary = () => {
    if (likeCount <= 0 || likePreview.length === 0) return null;
    const meId = user?.id;
    const namesToShow = likePreview.slice(0, likeCount === likePreview.length ? likePreview.length : 1);
    const remaining = likeCount - namesToShow.length;
    const labelFor = (u: LikePreviewUser) => (u.id === meId ? 'You' : u.fullName.split(' ')[0]);

    return (
      <>
        Liked by{' '}
        {namesToShow.map((u, idx) => (
          <React.Fragment key={u.id}>
            <span className="post-likes-preview-name">{labelFor(u)}</span>
            {idx < namesToShow.length - 1 && (idx === namesToShow.length - 2 && remaining === 0 ? ' and ' : ', ')}
          </React.Fragment>
        ))}
        {remaining > 0 && (
          <>
            {' '}and{' '}
            <span className="post-likes-preview-name">
              {remaining} other{remaining === 1 ? '' : 's'}
            </span>
          </>
        )}
      </>
    );
  };

  const loadComments = useCallback(async () => {
    if (commentsLoaded) return;
    try {
      const data = await postsService.getComments(post.id);
      setComments(data);
      setCommentsLoaded(true);
    } catch {
      // silently fail
    }
  }, [commentsLoaded, post.id]);

  const handleToggleComments = () => {
    const next = !showComments;
    setShowComments(next);
    if (next) {
      loadComments();
      setTimeout(() => commentInputRef.current?.focus(), 150);
    }
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = commentText.trim();
    if (!text || submittingComment) return;
    setSubmittingComment(true);
    try {
      const newComment = await postsService.addComment(post.id, text);
      setComments((prev) => [...prev, newComment]);
      setCommentCount((c) => c + 1);
      setCommentText('');
    } catch {
      // silently fail
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await postsService.deleteComment(post.id, commentId);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      setCommentCount((c) => Math.max(0, c - 1));
    } catch {
      // silently fail
    }
  };

  const handleCommentKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitComment(e as any);
    }
  };

  const renderContent = (content: string) => {
    if (!content) return null;

    const urlRegex = /(https?:\/\/[^\s]+)|(View in Documents:|View it in Documents:)\s*([^\s\n]+)/gi;
    const parts: Array<{ type: 'text' | 'url' | 'docLink'; value: string }> = [];
    let lastIndex = 0;
    let match;

    while ((match = urlRegex.exec(content)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ type: 'text', value: content.substring(lastIndex, match.index) });
      }
      if (match[1]) {
        parts.push({ type: 'url', value: match[1] });
      } else if (match[2] && match[3]) {
        parts.push({ type: 'text', value: match[2] + ' ' });
        parts.push({ type: 'docLink', value: match[3] });
      }
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < content.length) {
      parts.push({ type: 'text', value: content.substring(lastIndex) });
    }

    if (parts.length === 0) return <p className="post-text">{content}</p>;

    return (
      <p className="post-text">
        {parts.map((part, index) => {
          if (part.type === 'text') return <span key={index}>{part.value}</span>;
          if (part.type === 'url') {
            return (
              <a key={index} href={part.value} target="_blank" rel="noopener noreferrer"
                className="post-link" onClick={(e) => e.stopPropagation()}>
                {part.value}
              </a>
            );
          }
          if (part.type === 'docLink') {
            const urlMatch = part.value.match(/docId=([^&]+)&folderId=([^&\s\n]+)/);
            const docId = urlMatch ? urlMatch[1] : null;
            const folderId = urlMatch ? urlMatch[2] : null;
            return (
              <a key={index}
                href={docId && folderId ? `/documents?docId=${docId}&folderId=${folderId}` : '/documents'}
                className="post-link"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (docId && folderId) navigate(`/documents?docId=${docId}&folderId=${folderId}`);
                  else navigate('/documents');
                }}>
                {urlMatch ? 'View in Documents' : part.value}
              </a>
            );
          }
          return null;
        })}
      </p>
    );
  };

  const canDeleteComment = (comment: PostComment) =>
    isCoAdminOrAdmin(user?.role) || user?.id === comment.userId;

  return (
    <div className="post-card">
      {/* Header */}
      <div className="post-header">
        <div className="post-header-badges">
          <div className="post-department-badge" style={{ backgroundColor: departmentColor }}>
            {post.department || post.createdBy?.department || 'General'} Department
          </div>
          {post.accessScope === 'COMPANY' && (
            <span className="post-access-badge post-access-company">Home feed</span>
          )}
          {post.accessScope === 'BRANCH' && (
            <span className="post-access-badge post-access-branch">Branch access only</span>
          )}
          {post.accessScope === 'DEPARTMENT' && (
            <span className="post-access-badge post-access-dept">Department access only</span>
          )}
          {post.accessScope === 'DEPARTMENT_ONLY' && (
            <span className="post-access-badge post-access-dept">Department access only</span>
          )}
        </div>
        {canModify && (
          <div className="post-menu-container" ref={menuRef}>
            <button className="post-menu-btn" onClick={() => setShowMenu(!showMenu)}>•••</button>
            {showMenu && (
              <div className="post-menu-dropdown">
                <button onClick={handleEdit}>Edit</button>
                <button onClick={handleDelete} className="delete">Delete</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Author */}
      <div className="post-author">
        <div className="author-avatar" style={{ backgroundColor: departmentColor }}>
          {post.createdBy?.profilePicture ? (
            <img
              src={post.createdBy.profilePicture}
              alt={post.createdBy.fullName}
              loading="lazy"
              decoding="async"
            />
          ) : (
            getInitials(post.createdBy?.fullName || 'U')
          )}
        </div>
        <div className="author-info">
          <span className="author-name">{post.createdBy?.fullName}</span>
          <span className="post-time">{formatDateTime(post.createdAt)}</span>
        </div>
      </div>

      {/* Content */}
      <div className="post-content">
        <h3 className="post-title">{post.caption}</h3>
        {post.content && renderContent(post.content)}
      </div>

      {/* Media */}
      {mediaUrl && (
        <div className={`post-media${cdnIsVideo ? ' post-media--video' : ''}`}>
          {cdnIsVideo ? (
            <video
              ref={feedVideoRef}
              className="post-video"
              src={mediaUrl}
              controls
              playsInline
              muted
              loop
              preload="auto"
            />
          ) : (
            <img
              src={mediaUrl}
              alt={post.caption}
              className="post-image-clickable"
              onClick={() => setShowImageModal(true)}
            />
          )}
        </div>
      )}

      {/* Image lightbox (images only) */}
      {showImageModal && mediaUrl && !cdnIsVideo && createPortal(
        <div className="image-modal-overlay" onClick={() => setShowImageModal(false)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="image-modal-close" onClick={() => setShowImageModal(false)} aria-label="Close">×</button>
            <img src={mediaUrl} alt={post.caption || 'Post image'} className="enlarged-image" />
            {post.caption && (
              <div className="image-modal-caption"><p>{post.caption}</p></div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && createPortal(
        <div className="post-delete-modal-overlay" onClick={cancelDelete}>
          <div className="post-delete-modal-content" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="post-delete-modal-header">
              <h3>Delete Post</h3>
              <button className="post-delete-modal-close" onClick={cancelDelete} aria-label="Close">×</button>
            </div>
            <div className="post-delete-modal-body">
              <p>Are you sure you want to delete this post?</p>
            </div>
            <div className="post-delete-modal-footer">
              <button type="button" className="post-delete-cancel" onClick={cancelDelete}>Cancel</button>
              <button type="button" className="post-delete-confirm" onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Likes preview row — Instagram-style */}
      {likeCount > 0 && (
        <div className="post-likes-preview">
          {likePreview.length > 0 && (
            <button
              type="button"
              className="post-likes-preview-avatars"
              onClick={openLikesModal}
              aria-label="View all likes"
            >
              {likePreview.map((u) => (
                <span
                  key={u.id}
                  className="post-likes-preview-avatar"
                  style={{ backgroundColor: getDepartmentColor(u.department || '') }}
                  title={u.fullName}
                >
                  {u.profilePicture ? (
                    <img src={u.profilePicture} alt="" loading="lazy" decoding="async" />
                  ) : (
                    <span className="post-likes-preview-initials">{getInitials(u.fullName)}</span>
                  )}
                </span>
              ))}
            </button>
          )}
          <button
            type="button"
            className="post-likes-preview-text"
            onClick={openLikesModal}
            aria-label={`See everyone who liked this post (${likeCount} ${likeCount === 1 ? 'like' : 'likes'})`}
          >
            {likePreview.length > 0 ? (
              renderInstagramSummary()
            ) : (
              <>
                <span className="post-likes-preview-name">{likeCount}</span>{' '}
                {likeCount === 1 ? 'like' : 'likes'}
              </>
            )}
          </button>
        </div>
      )}

      {/* Likes list modal (lazy-loaded) */}
      {showLikesModal && createPortal(
        <div className="post-likes-modal-overlay" onClick={closeLikesModal}>
          <div
            className="post-likes-modal-content"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby={`likes-modal-title-${post.id}`}
          >
            <div className="post-likes-modal-header">
              <h3 id={`likes-modal-title-${post.id}`}>Likes</h3>
              <button
                type="button"
                className="post-likes-modal-close"
                onClick={closeLikesModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="post-likes-modal-body">
              {likersLoading && (
                <div className="post-likes-modal-loading">
                  <span className="comments-spinner" />
                </div>
              )}
              {!likersLoading && likersError && (
                <div className="post-likes-modal-error">
                  <p>{likersError}</p>
                  <button type="button" onClick={loadLikers}>Retry</button>
                </div>
              )}
              {!likersLoading && !likersError && likers.length === 0 && likersLoaded && (
                <p className="post-likes-modal-empty">No likes yet.</p>
              )}
              {!likersLoading && !likersError && likers.length > 0 && (
                <ul className="post-likes-modal-list">
                  {likers.map((liker) => (
                    <li key={liker.id} className="post-likes-modal-item">
                      <div
                        className="post-likes-modal-avatar"
                        style={{ backgroundColor: getDepartmentColor(liker.department || '') }}
                      >
                        {liker.profilePicture ? (
                          <img src={liker.profilePicture} alt={liker.fullName} loading="lazy" decoding="async" />
                        ) : (
                          getInitials(liker.fullName)
                        )}
                      </div>
                      <div className="post-likes-modal-meta">
                        <span className="post-likes-modal-name">
                          {liker.fullName}
                          {liker.id === user?.id && <span className="post-likes-modal-you"> (You)</span>}
                        </span>
                        {(liker.designation || liker.department) && (
                          <span className="post-likes-modal-sub">
                            {[liker.designation, liker.department].filter(Boolean).join(' • ')}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Reaction Bar */}
      <div className="post-reaction-bar">
        <button
          className={`reaction-btn like-btn${liked ? ' liked' : ''}`}
          onClick={handleLike}
          disabled={likeLoading}
          aria-label={liked ? 'Unlike' : 'Like'}
        >
          <span className="reaction-icon">
            {liked ? (
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
            )}
          </span>
          <span className="reaction-count">{likeCount > 0 ? likeCount : ''}</span>
          <span className="reaction-label">Like</span>
        </button>

        <button
          className={`reaction-btn comment-btn${showComments ? ' active' : ''}`}
          onClick={handleToggleComments}
          aria-label="Comments"
        >
          <span className="reaction-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </span>
          <span className="reaction-count">{commentCount > 0 ? commentCount : ''}</span>
          <span className="reaction-label">Comment</span>
        </button>

        <span className="post-category-pill">{post.category}</span>
      </div>

      {/* Comments Section */}
      {showComments && (
        <div className="post-comments-section">
          {/* Comment Input */}
          <form className="comment-form" onSubmit={handleSubmitComment}>
            <div className="comment-input-avatar" style={{ backgroundColor: getDepartmentColor(user?.department || '') }}>
              {user?.profilePicture ? (
                <img
                  src={user.profilePicture}
                  alt={user.fullName}
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                getInitials(user?.fullName || 'U')
              )}
            </div>
            <div className="comment-input-wrapper">
              <textarea
                ref={commentInputRef}
                className="comment-input"
                placeholder="Write a comment…"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={handleCommentKeyDown}
                rows={1}
              />
              {commentText.trim() && (
                <button
                  type="submit"
                  className="comment-send-btn"
                  disabled={submittingComment}
                  aria-label="Send comment"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                    <line x1="22" y1="2" x2="11" y2="13"/>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                </button>
              )}
            </div>
          </form>

          {/* Comment List */}
          {!commentsLoaded && (
            <div className="comments-loading">
              <span className="comments-spinner" />
            </div>
          )}
          {commentsLoaded && comments.length === 0 && (
            <p className="no-comments">No comments yet. Be the first!</p>
          )}
          {commentsLoaded && comments.length > 0 && (
            <div className="comments-list">
              {comments.map((comment) => (
                <div key={comment.id} className="comment-item">
                  <div
                    className="comment-avatar"
                    style={{ backgroundColor: getDepartmentColor(comment.user?.department || '') }}
                  >
                    {comment.user?.profilePicture ? (
                      <img src={comment.user.profilePicture} alt={comment.user.fullName} />
                    ) : (
                      getInitials(comment.user?.fullName || 'U')
                    )}
                  </div>
                  <div className="comment-bubble">
                    <div className="comment-header">
                      <span className="comment-author">{comment.user?.fullName || 'User'}</span>
                      <span className="comment-time">{formatDateTime(comment.createdAt)}</span>
                      {canDeleteComment(comment) && (
                        <button
                          className="comment-delete-btn"
                          onClick={() => handleDeleteComment(comment.id)}
                          aria-label="Delete comment"
                          title="Delete"
                        >
                          ×
                        </button>
                      )}
                    </div>
                    <p className="comment-text">{comment.text}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(PostCard);
